import React, { Component } from 'react';
import { firebaseRef, errorAlert } from '../util/helperFunctions';
import { Title } from 'bloomer/lib/elements/Title';
import Bold from '../elements/Bold';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Input } from 'bloomer/lib/elements/Form/Input';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Button } from 'bloomer/lib/elements/Button';
import { RouteComponentProps, Link } from '@reach/router';
import { AnimalOptionsConsumer } from '../DataProviders/AnimalOptions';
import { Container } from 'bloomer/lib/layout/Container';
import { Box } from 'bloomer/lib/elements/Box';
import LoadingSpinner from '../elements/LoadingSpinner';
import { Columns } from 'bloomer/lib/grid/Columns';
import { Column } from 'bloomer/lib/grid/Column';
import styled from 'styled-components';
import TitleCase from '../elements/TitleCase';
import { Level } from 'bloomer/lib/components/Level/Level';
import MarginlessTitle from '../elements/MarginlessTitle';
import { LevelRight } from 'bloomer/lib/components/Level/LevelRight';
import { LevelItem } from 'bloomer/lib/components/Level/LevelItem';
import Spacer from '../elements/Spacer';
import { Image } from 'bloomer/lib/elements/Image';
import cat from '../assets/catIcon.svg';
import dog from '../assets/dogIcon.svg';
import Conditional from '../components/Conditional';
import { Icon } from 'bloomer/lib/elements/Icon';

interface Params {
  animalType: animalType;
}

interface PropTypes extends RouteComponentProps<Params> {}

interface StateTypes {
  newEnrich: string;
  newColorCode: string;
  [Key: string]: string;
}

const Tile = styled(Box)`
  &.box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 12px 0;
  }
`;

class AnimalAdminOptions extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    newEnrich: '',
    newColorCode: '',
  };

  handleChange = (e: React.FormEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement;
    this.setState({
      [target.name]: target.value,
    } as any);
  };

  addToEnrichOrColor = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { animalType } = this.props;
    const target = e.target as HTMLFormElement;
    const formInputValue = this.state[target.id].toLowerCase();
    let enrichOrColor;
    if (target.id === 'newEnrich') {
      enrichOrColor = 'enrichmentTypes';
    } else if (target.id === 'newColorCode') {
      enrichOrColor = 'colorCodes';
    }
    const targetRef = firebaseRef(
      `/animalOptions/${animalType}/${enrichOrColor}`
    );
    targetRef.push(formInputValue).then(success => console.log('success'));
    // reset inputs
    this.setState({
      newEnrich: '',
      newColorCode: '',
    });
  };

  deleteRef(ref: string) {
    const doomedRef = firebaseRef(ref);
    doomedRef
      .remove()
      .then(success => console.log('success'))
      .catch(err => errorAlert(err));
  }

  render() {
    // tslint:disable:jsx-no-lambda
    return (
      <AnimalOptionsConsumer>
        {({ animalOptions }) => {
          const { animalType } = this.props;
          if (!animalOptions || !animalType) {
            return (
              <Container>
                <Box>
                  <LoadingSpinner />
                </Box>
              </Container>
            );
          }
          const options = animalOptions[animalType];
          return (
            <Container>
              <Box>
                <Level>
                  <MarginlessTitle isSize={1}>
                    <Bold>Admin Options:</Bold>{' '}
                    <TitleCase>{animalType}</TitleCase>
                  </MarginlessTitle>
                  <LevelRight>
                    <LevelItem>
                      <Link to="/admin/general">
                        <Button isColor="primary">
                          <Icon isSize="medium" className="fa fa-cog" />{' '}
                          <Spacer>General Options</Spacer>
                        </Button>
                      </Link>
                    </LevelItem>
                    <LevelItem>
                      <Conditional check={animalType !== 'dog'}>
                        <Link to="/admin/dog">
                          <Button isColor="primary">
                            <Image isSize="24x24" src={dog} />{' '}
                            <Spacer>Dog Options</Spacer>
                          </Button>
                        </Link>
                      </Conditional>
                    </LevelItem>
                    <LevelItem>
                      <Conditional check={animalType !== 'cat'}>
                        <Link to="/admin/cat">
                          <Button isColor="primary">
                            <Image isSize="24x24" src={cat} />{' '}
                            <Spacer>Cat Options</Spacer>
                          </Button>
                        </Link>
                      </Conditional>
                    </LevelItem>
                  </LevelRight>
                </Level>
                <Columns>
                  <Column>
                    <Title tag="h2">Types of Enrichment:</Title>
                    <ul>
                      {Object.entries(options.enrichmentTypes).map(
                        ([key, enrich]) => (
                          <li key={key}>
                            <Tile>
                              {enrich}{' '}
                              <Button
                                isColor="danger"
                                onClick={e =>
                                  this.deleteRef(
                                    `/animalOptions/${animalType}/enrichmentTypes/${key}`
                                  )
                                }
                              >
                                Delete
                              </Button>
                            </Tile>
                          </li>
                        )
                      )}
                    </ul>
                    <form id="newEnrich" onSubmit={this.addToEnrichOrColor}>
                      <Field>
                        <Label htmlFor="newEnrich">Type to Add:</Label>
                        <Control>
                          <Input
                            name="newEnrich"
                            type="text"
                            onChange={this.handleChange}
                            value={this.state.newEnrich}
                          />
                        </Control>
                      </Field>
                      <Field>
                        <Control>
                          <Button type="submit" isColor="primary">
                            Add
                          </Button>
                        </Control>
                      </Field>
                    </form>
                  </Column>
                  <Column>
                    <Title tag="h2">Color Codes:</Title>
                    <ul>
                      {Object.entries(options.colorCodes).map(
                        ([key, color]) => (
                          <li key={key}>
                            <Tile>
                              {color}{' '}
                              <Button
                                isColor="danger"
                                onClick={e =>
                                  this.deleteRef(
                                    `/animalOptions/${animalType}/colorCodes/${key}`
                                  )
                                }
                              >
                                Delete
                              </Button>
                            </Tile>
                          </li>
                        )
                      )}
                    </ul>
                    <form onSubmit={this.addToEnrichOrColor} id="newColorCode">
                      <Field>
                        <Label htmlFor="newColorCode">Color Code to Add:</Label>
                        <Control>
                          <Input
                            name="newColorCode"
                            type="text"
                            onChange={this.handleChange}
                            value={this.state.newColorCode}
                          />
                        </Control>
                      </Field>
                      <Field>
                        <Control>
                          <Button type="submit" isColor="primary">
                            Add
                          </Button>
                        </Control>
                      </Field>
                    </form>
                    {/* <ManageSpecialNeeds animalType={animalType} /> */}
                  </Column>
                </Columns>
              </Box>
            </Container>
          );
        }}
      </AnimalOptionsConsumer>
    );
  }
}

export default AnimalAdminOptions;
